<script setup>
defineProps({
  items: {
    type: Array,
    default: () => [],
  },
});

defineEmits(["view", "edit"]);

const formatCurrency = (value) => {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(value);
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

const unitTypeText = (type) => {
  const map = { house: "บ้าน", condo: "ห้องชุด", shop: "ร้านค้า" };
  return map[type] || type;
};

const occupancyText = (status) => {
  const map = { occupied: "มีผู้พักอาศัย", vacant: "ว่าง" };
  return map[status] || status;
};

const paymentStatusText = (status) => {
  const map = {
    paid: "ชำระแล้ว",
    partial: "ชำระบางส่วน",
    overdue: "ค้างชำระ",
    vacant: "ว่าง",
  };
  return map[status] || status;
};

const paymentBadgeClass = (status) => {
  const map = {
    paid: "text-bg-success",
    partial: "text-bg-warning",
    overdue: "text-bg-danger",
    vacant: "text-bg-secondary",
  };
  return map[status] || "text-bg-light";
};

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return "";
  const stringValue = String(value).replace(/"/g, '""');
  return `"${stringValue}"`;
};

const exportToCsv = (items) => {
  if (!items || !items.length) {
    alert("ไม่มีข้อมูลสำหรับ export");
    return;
  }

  const headers = [
    "รหัสยูนิต",
    "บ้านเลขที่",
    "อาคาร",
    "โซน",
    "ประเภท",
    "สถานะการพักอาศัย",
    "ลูกบ้าน",
    "พื้นที่ (ตร.ม.)",
    "ค่าส่วนกลาง/เดือน",
    "สถานะชำระเงิน",
    "ยอดค้าง",
    "ชำระล่าสุด",
  ];

  const rows = items.map((unit) => [
    unit.unitCode,
    unit.houseNo,
    unit.building,
    unit.zone,
    unitTypeText(unit.unitType),
    occupancyText(unit.occupancyStatus),
    unit.residentName || "ยังไม่มีผู้พักอาศัย",
    unit.area ?? 0,
    unit.monthlyFee ?? 0,
    paymentStatusText(unit.paymentStatus),
    unit.balance ?? 0,
    unit.lastPaymentDate ? formatDate(unit.lastPaymentDate) : "-",
  ]);

  const csvContent = [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) => row.map(escapeCsvValue).join(",")),
  ].join("\n");

  // ใส่ BOM เพื่อให้ Excel เปิดภาษาไทยได้ถูกต้อง
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const now = new Date();

  const fileName = `units-export-${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}.csv`;

  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

</script>

<template>
  <div class="card border-0 shadow-sm rounded-4">
    <div class="card-body p-0">
      <div
        class="px-4 pt-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
      >
        <div>
          <h5 class="fw-bold mb-1">รายการยูนิตทั้งหมด</h5>
          <div class="text-muted small">
            พบทั้งหมด {{ items.length }} รายการ
          </div>
        </div>

        <button
          type="button"
          class="btn btn-outline-danger border-danger-subtle rounded-3 shadow-sm"
          :disabled="!items.length"
          @click="exportToCsv(items)"
        >
          <i class="bi bi-download me-1"></i>Export
        </button>
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-4">รหัสยูนิต</th>
              <th>บ้านเลขที่</th>
              <th>อาคาร / โซน</th>
              <th>ประเภท</th>
              <th>ลูกบ้าน</th>
              <th class="text-end">พื้นที่</th>
              <th>กลาง/เดือน</th>
              <th>สถานะ</th>
              <th class="text-end">ยอดค้าง</th>
              <th class="text-center pe-4">จัดการ</th>
            </tr>
          </thead>

          <tbody v-if="items.length">
            <tr v-for="unit in items" :key="unit.id">
              <td class="ps-4">
                <div class="fw-semibold">{{ unit.unitCode }}</div>
                <div class="small text-muted">
                  {{ occupancyText(unit.occupancyStatus) }}
                </div>
              </td>

              <td>{{ unit.houseNo }}</td>

              <td>
                <div>{{ unit.building }}</div>
                <div class="small text-muted">{{ unit.zone }}</div>
              </td>

              <td>
                <span class="badge rounded-pill text-bg-light border text-dark">
                  {{ unitTypeText(unit.unitType) }}
                </span>
              </td>

              <td>
                <div v-if="unit.residentName" class="fw-medium">
                  {{ unit.residentName }}
                </div>
                <div v-else class="text-muted">ยังไม่มีผู้พักอาศัย</div>

                <div class="small text-muted" v-if="unit.lastPaymentDate">
                  ชำระล่าสุด {{ formatDate(unit.lastPaymentDate) }}
                </div>
              </td>

              <td class="text-end">{{ unit.area }} ตร.ม.</td>

              <td class="text-end">{{ formatCurrency(unit.monthlyFee) }}</td>

              <td>
                <span
                  class="badge rounded-pill"
                  :class="paymentBadgeClass(unit.paymentStatus)"
                >
                  {{ paymentStatusText(unit.paymentStatus) }}
                </span>
              </td>

              <td class="text-end">
                <span
                  :class="
                    unit.balance > 0
                      ? 'text-danger fw-semibold'
                      : 'text-success fw-semibold'
                  "
                >
                  {{ formatCurrency(unit.balance) }}
                </span>
              </td>

              <td class="text-center pe-4">
                <div class="btn-group">
                  <button
                    type="button"
                    class="btn btn-sm btn-light border"
                    @click="$emit('view', unit)"
                    title="ดูรายละเอียด"
                  >
                    <i class="bi bi-eye text-primary-emphasis"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-light border"
                    @click="$emit('edit', unit)"
                    title="แก้ไข"
                  >
                    <i class="bi bi-pencil-square text-warning-emphasis"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td colspan="10" class="text-center py-5">
                <div class="text-muted">
                  <i class="bi bi-inbox fs-2 d-block mb-2"></i>
                  ไม่พบข้อมูลตามเงื่อนไขที่ค้นหา
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.table th {
  white-space: nowrap;
  font-size: 0.9rem;
}

.table td {
  vertical-align: middle;
}
</style>